/**
 * V3.0 ClientSelector — Persistent global client selector in AdminClient sidebar.
 *
 * Features:
 * - Global view (all clients aggregated) vs Client-focused view
 * - Recent clients (last 5) stored in localStorage
 * - Search by name/status
 * - Status badges with relationship lifecycle indicators
 * - URL param sync: ?clientId=xxx
 */
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Building2, Search, X, ChevronDown, Globe, Clock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { useClientContext } from '@/stores/client-context.store'
import apiClient from '@/lib/api/client'

const CLIENT_MGMT_URL = import.meta.env.VITE_CLIENT_MGMT_URL || 'https://client-management.sgrc.api.i2tdigital.com'
const RECENT_CLIENTS_KEY = 'sgrc-recent-clients'
const MAX_RECENT = 5

type ClientStatus = 'prospect' | 'active' | 'archived' | 'suspended'

interface ClientItem {
  id: string
  name: string
  status: ClientStatus
  industry?: string
  relationship_status?: string
}

const statusConfig: Record<ClientStatus, { label: string; color: string; icon: React.ElementType }> = {
  active: { label: 'Actif', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  prospect: { label: 'Prospect', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  archived: { label: 'Archivé', color: 'bg-gray-100 text-gray-600', icon: X },
  suspended: { label: 'Suspendu', color: 'bg-red-100 text-red-700', icon: AlertCircle },
}

function getRecentClients(): ClientItem[] {
  try {
    const stored = localStorage.getItem(RECENT_CLIENTS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function addRecentClient(client: ClientItem) {
  try {
    const recent = getRecentClients().filter((c) => c.id !== client.id)
    recent.unshift(client)
    localStorage.setItem(RECENT_CLIENTS_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
  } catch {
    // localStorage may be unavailable
  }
}

export function ClientSelector({ collapsed = false }: { collapsed?: boolean }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeClientId, activeClientName, setActiveClient, clearContext } = useClientContext()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'global' | 'client'>(() =>
    searchParams.get('clientId') ? 'client' : 'global'
  )
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch client list
  const { data: clientsData, isLoading } = useQuery({
    queryKey: ['client-selector-list', search],
    queryFn: async () => {
      const params: any = { page: 1, page_size: 50 }
      if (search) params.search = search
      const { data } = await apiClient.get(`${CLIENT_MGMT_URL}/api/v1/clients`, { params })
      return data?.items || []
    },
    enabled: isOpen,
    staleTime: 30000,
  })

  // Sync from URL param on mount
  useEffect(() => {
    const urlClientId = searchParams.get('clientId')
    if (urlClientId && urlClientId !== activeClientId) {
      setActiveClient(urlClientId)
      setViewMode('client')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  const handleSelectClient = async (client: ClientItem) => {
    addRecentClient(client)
    await setActiveClient(client.id, client.name)
    setViewMode('client')
    setIsOpen(false)
    setSearch('')
    // Update URL param
    const params = new URLSearchParams(searchParams)
    params.set('clientId', client.id)
    setSearchParams(params)
  }

  const handleGlobalMode = () => {
    clearContext()
    setViewMode('global')
    setIsOpen(false)
    const params = new URLSearchParams(searchParams)
    params.delete('clientId')
    setSearchParams(params)
  }

  const recentClients = getRecentClients()
  const clients: ClientItem[] = clientsData || []
  const displayedClients = clients.length > 0 ? clients : recentClients

  if (collapsed) {
    return (
      <div className="px-2">
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'w-full h-10',
            activeClientId ? 'text-blue-500' : 'text-muted-foreground'
          )}
          onClick={() => setIsOpen(!isOpen)}
          title={activeClientName || 'Sélectionner un client'}
        >
          <Building2 className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div ref={dropdownRef} className="relative px-3 py-2">
      {/* Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors',
          viewMode === 'client' && activeClientId
            ? 'border-blue-400/40 bg-blue-500/8 text-[hsl(var(--sidebar-fg))]'
            : 'border-dashed border-[hsl(var(--sidebar-fg))]/30 text-[hsl(var(--sidebar-fg))]/85 hover:border-primary/40 hover:text-[hsl(var(--sidebar-fg))]',
        )}
      >
        {viewMode === 'global' ? (
          <Globe className="h-4 w-4 text-primary shrink-0" />
        ) : (
          <Building2 className={cn('h-4 w-4 shrink-0', activeClientId ? 'text-blue-500' : 'text-muted-foreground')} />
        )}
        <span className="flex-1 truncate text-left">
          {viewMode === 'global' ? 'Vue Globale' : (activeClientName || 'Sélectionner client...')}
        </span>
        <ChevronDown className={cn('h-3 w-3 shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-3 right-3 top-full z-50 mt-1 rounded-lg border bg-popover shadow-lg">
          {/* Global view option */}
          <button
            onClick={handleGlobalMode}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2.5 text-sm transition-colors hover:bg-accent rounded-t-lg',
              viewMode === 'global' && 'bg-primary/5 text-primary font-medium'
            )}
          >
            <Globe className="h-4 w-4 text-primary" />
            <span>Vue Globale (tous clients)</span>
            {viewMode === 'global' && <CheckCircle className="ml-auto h-3 w-3 text-primary" />}
          </button>

          <div className="border-t" />

          {/* Search */}
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Rechercher un client..."
                className="w-full rounded-md border bg-background pl-8 pr-3 py-1.5 text-xs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {/* Client list */}
          <ScrollArea className="max-h-56">
            {isLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && displayedClients.length === 0 && (
              <p className="py-3 text-center text-xs text-muted-foreground">Aucun client trouvé</p>
            )}
            {!isLoading && displayedClients.length > 0 && (
              <div className="pb-1">
                {search === '' && recentClients.length > 0 && clients.length === 0 && (
                  <p className="px-3 py-1 text-xs text-muted-foreground">Récents</p>
                )}
                {displayedClients.map((client) => {
                  const statusCfg = statusConfig[client.status as ClientStatus] || statusConfig.active
                  const isActive = activeClientId === client.id
                  return (
                    <button
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      className={cn(
                        'flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-accent',
                        isActive && 'bg-primary/5'
                      )}
                    >
                      <Building2 className={cn('h-4 w-4 shrink-0', isActive ? 'text-blue-500' : 'text-muted-foreground')} />
                      <div className="flex-1 min-w-0 text-left">
                        <p className={cn('truncate text-sm', isActive && 'font-medium text-primary')}>{client.name}</p>
                        {client.industry && (
                          <p className="truncate text-xs text-muted-foreground">{client.industry}</p>
                        )}
                      </div>
                      <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-xs font-medium', statusCfg.color)}>
                        {statusCfg.label}
                      </span>
                      {isActive && <CheckCircle className="h-3 w-3 text-primary shrink-0" />}
                    </button>
                  )
                })}
              </div>
            )}
          </ScrollArea>

          {/* Clear context */}
          {activeClientId && (
            <div className="border-t p-2">
              <button
                onClick={handleGlobalMode}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <X className="h-3 w-3" />
                Effacer la sélection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
